import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby";

import SeoData from "../assets/seo/seo.json";
import { blogPosts } from "~content/blog/blogPosts";

const monthMap = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Sept: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
};

const normalizePath = (path = "") => path.replace(/\/$/, "");

const getAbsoluteUrl = (value, siteUrl) => {
    if (!value) return undefined;

    try {
        return new URL(value, siteUrl).toString();
    } catch (error) {
        return undefined;
    }
};

const parseBlogDateToISO = (value = "") => {
    const match = value.trim().match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);

    if (!match) return undefined;

    const [, day, monthLabel, year] = match;
    const month = monthMap[monthLabel];

    if (!month) return undefined;

    return `${year}-${month}-${day.padStart(2, "0")}T00:00:00+08:00`;
};

function SEO({ description = "", lang, meta, title, seo_name="",keywords="", pathname="/",crumbs }) {

     const { site } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  const siteUrl = site.siteMetadata.siteUrl;
  const canonical = new URL(pathname, siteUrl).toString();

    // Find the SEO entry that matches seo_name
    const seoEntry = (SeoData.length>0 ? SeoData.find((item) => item.name === seo_name):'');
    const matchedBlogPost = blogPosts.find(post => normalizePath(post.to) === normalizePath(pathname));

    const metaTitle = seoEntry?.label || title;
    const metaTitlePrefix = seoEntry?.title || (title + " | CommonTown ");
    const metaDescription = seoEntry?.description || description;
    const metaKeywords = seoEntry?.keywords || keywords;
    const metaImage = getAbsoluteUrl(
        seoEntry?.og_image || matchedBlogPost?.icon?.default || matchedBlogPost?.icon,
        siteUrl
    );
    const isBlogPost = seoEntry?.type === "blog" || Boolean(matchedBlogPost);
    const publishedAt = parseBlogDateToISO(matchedBlogPost?.date) || seoEntry?.publishedAt;
    const updatedAt = seoEntry?.updatedAt || publishedAt;

    // Build Breadcrumb Schema Markup
    const buildBreadcrumbSchema = (crumbs = []) => {
        if(crumbs.length>0){
            const list = [
                    { text: "Home", to: "/" },
                    ...crumbs.filter((c, i) => !(i === 0 && (c.text?.toLowerCase() === "home" || c.to === "/")))
            ];
                            
            return {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": list.map((c, index) => (
                    {
                        "@type": "ListItem",
                        position: index + 1,
                        name: c.text,
                        ...(c.to ? { item: getAbsoluteUrl(c.to, siteUrl) } : {})
                    }
                ))
            };
        }
    }

    const schemaCrumb = buildBreadcrumbSchema(crumbs);

    const schemaMarkup = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "CommonTown Pte Ltd",
        "image": "https://media.licdn.com/dms/image/v2/C4E0BAQHLS9pI6iwe3w/company-logo_200_200/company-logo_200_200/0/1631336183147?e=2147483647&v=beta&t=FfLKuGAVfw8RPm2LRkyhtVT_56bHIDIwhuoqwctPT38",
        "@id": "",
        "url": "https://www.commontown.com/",
        "telephone": "6848 8900",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "#08-72 Vertex Tower A, 33 Ubi Avenue 3",
            "addressLocality": "Singapore",
            "postalCode": "408868",
            "addressCountry": "SG"
        },
        "description": "CommonTown develops educational solutions that leverage deep technology, in-depth research, and a thorough understanding of our users.",
        "openingHours": [
            "Monday 09:00-18:00",
            "Tuesday 09:00-18:00",
            "Wednesday 09:00-18:00",
            "Thursday 09:00-18:00",
            "Friday 09:00-18:00",
            "Saturday Closed",
            "Sunday Closed"
        ],
        "sameAs": [
            "https://www.linkedin.com/company/commontown-pte-ltd",
            "https://www.facebook.com/CommonTown/",
            "https://www.youtube.com/user/CommonTownvideos"
        ]
    }

    const articleSchema = isBlogPost ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": metaTitle,
        "description": metaDescription,
        ...(metaImage ? { "image": [metaImage] } : {}),
        ...(publishedAt ? { "datePublished": publishedAt } : {}),
        ...(updatedAt ? { "dateModified": updatedAt } : {}),
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": canonical
        },
        "author": {
            "@type": seoEntry?.author?.name === "CommonTown" ? "Organization" : "Person",
            "name": seoEntry?.author?.name || "CommonTown",
            "url": seoEntry?.author?.url || getAbsoluteUrl("/about-us/", siteUrl)
        },
        "publisher": {
            "@type": "Organization",
            "name": "CommonTown",
            "logo": {
                "@type": "ImageObject",
                "url": "https://media.licdn.com/dms/image/v2/C4E0BAQHLS9pI6iwe3w/company-logo_200_200/company-logo_200_200/0/1631336183147?e=2147483647&v=beta&t=FfLKuGAVfw8RPm2LRkyhtVT_56bHIDIwhuoqwctPT38"
            }
        }
    } : null;

    const structuredData = [schemaMarkup, schemaCrumb, articleSchema].filter(Boolean);
    
    return (
        <Helmet
            htmlAttributes={{
                lang,
            }}
            title={metaTitle}
            // This defines a title format where %s is replaced by the value of title.
            titleTemplate={metaTitlePrefix} 
            meta={[
                {
                    name: `description`,
                    content: metaDescription,
                },
                {
                    name: `keywords`,
                    content: metaKeywords,
                },
                {
                    property: `og:title`,
                    content: metaTitle,
                },
                {
                    property: `og:description`,
                    content: metaDescription,
                },
                {
                    property: `og:type`,
                    content: isBlogPost ? `article` : `website`,
                },
                {
                    property: `og:url`,
                    content: canonical,
                },
                ...(metaImage ? [{
                    property: `og:image`,
                    content: metaImage,
                }] : []),
                ...(publishedAt ? [{
                    property: `article:published_time`,
                    content: publishedAt,
                }] : []),
                ...(updatedAt ? [{
                    property: `article:modified_time`,
                    content: updatedAt,
                }] : []),
                {
                    name: `twitter:card`,
                    content: metaImage ? `summary_large_image` : `summary`,
                },
                {
                    name: `twitter:creator`,
                    content: "@Hui_Lii",
                },
                {
                    name: `twitter:title`,
                    content: metaTitle,
                },
                {
                    name: `twitter:description`,
                    content: metaDescription,
                },
                ...(metaImage ? [{
                    name: `twitter:image`,
                    content: metaImage,
                }] : []),
                {
                    name: `google-site-verification`,
                    content: `LHkDK8E0VYxApd9v11rzRO5gau-i7ii0h2MUq2n0cug`  
                }
            ].concat(meta)}
        >
             <link rel="canonical" href={canonical} />
            {structuredData.length > 0 && (
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            )}
        </Helmet>
    )
}

SEO.defaultProps = {
    lang: `en`,
    meta: [],
    description: ``,
}

SEO.propTypes = {
    description: PropTypes.string,
    lang: PropTypes.string,
    meta: PropTypes.arrayOf(PropTypes.object),
    title: PropTypes.string.isRequired,
}

export default SEO
